import { useMemo } from 'react';
import { AlertTriangle, Check, Circle, PenLine, type LucideIcon } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { format, isValid, parse } from 'date-fns';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDateFnsLocale } from '@/lib/format';
import { formatDate, parseDateString, toOrgDateOnly } from '@/utils/dateUtils';
import { useCompletionWarnings, useSettlingInBillingPreview } from '@/hooks/useSettlingIn';
import { useChildRelationships } from '@/hooks/useChildContactRelationships';
import { useModuleGate } from '@/hooks/useAccess';
import { RELATIONSHIP_READ_MODULES } from '@/lib/modules';
import { useNotAvailableForRoleLabel } from '@/components/auth/ModuleUnavailableHint';
import { settlingin_CompletePlanWarningDTO } from '@/types/settlingIn';
import type { SettlingInPlanDetail } from '@/types/settlingIn';

export type ClosureTab = 'overview' | 'days';

interface ClosureChecklistCardProps {
  plan: SettlingInPlanDetail;
  onComplete: () => void;
  onSignOff: () => void;
  onGoToTab: (tab: ClosureTab) => void;
  className?: string;
}

type RequirementState = 'met' | 'unmet';

interface Requirement {
  key: string;
  label: string;
  state: RequirementState;
  /** What a met item shows: date, count, or the resolved actor. */
  metDetail?: string;
  actionLabel?: string;
  /** Accessible name, when the visible label carries data rather than the destination. */
  actionAriaLabel?: string;
  onAction?: () => void;
  /**
   * Reopens the recording dialog for an already-met requirement, which is where
   * its edit AND undo live. Only completion and sign-off have one: the header
   * buttons that record them are hidden once recorded, so without this the event
   * would be uncorrectable and un-undoable.
   */
  onEdit?: () => void;
  editAriaLabel?: string;
}

const STATE_ICON: Record<RequirementState, LucideIcon> = {
  met: Check,
  unmet: Circle,
};

/**
 * Always-visible checklist of every closure requirement, replacing the prose
 * Status card that used to sit at the very bottom of the Overview tab.
 *
 * Answers "why can this plan not be billed?" without opening every tab. Each
 * requirement renders met (glyph + date/actor where a signal exists) or unmet
 * (a button that jumps to the fix).
 *
 * NEVER hard-gates: Complete and Sign Off stay enabled regardless of any other
 * requirement's state — completion warnings are advisory by design and must
 * never block completion. There is likewise no "bill" affordance: billing is
 * automatic -- invoices are held while the plan is open and start on the first
 * billing day F (#1697; the first present settling-in day for a subsidised
 * contract, #1703).
 */
export function ClosureChecklistCard({
  plan,
  onComplete,
  onSignOff,
  onGoToTab,
  className,
}: ClosureChecklistCardProps) {
  const { i18n } = useLingui();

  // Nil-normalize FIRST. Both arrays are optional in the generated type and this
  // card is a new consumer of them — a `null` from the Go-nil-slice→JSON-null
  // footgun would blank the whole detail page. Every requirement below derives
  // from these locals, never from the raw optional.
  //
  // Memoized because `?? []` mints a fresh array on every render, which would
  // churn the `missingActuals` dep array below on each one.
  const scheduleEntries = useMemo(() => plan.schedule_entries ?? [], [plan.schedule_entries]);
  const trackingEntries = useMemo(() => plan.tracking_entries ?? [], [plan.tracking_entries]);

  // A FAILED warnings fetch is not an empty warning set: `data` is `undefined`
  // in both cases, so the `?? []` below collapses them and the card would render
  // a clean, advisory-free closure state off a 500 — the most reassuring thing it
  // could possibly say, and false. Track the error separately.
  //
  // Unlike CompleteDialog, this card does NOT gate on it: it records nothing, so
  // there is no acknowledgment to defeat. Complete and Sign Off stay enabled, per
  // the never-hard-gates rule above.
  const { data: warningsData, isError: warningsFailed } = useCompletionWarnings(
    plan.id ?? '',
    !!plan.id,
  );
  const warnings = warningsData?.warnings ?? [];

  // REL_READ (#1670): without it the sign-off contact renders as the role hint.
  const canReadRelationships = useModuleGate(RELATIONSHIP_READ_MODULES);
  const notAvailableLabel = useNotAvailableForRoleLabel();
  const { data: relationships } = useChildRelationships(
    canReadRelationships ? (plan.child_id ?? '') : '',
    canReadRelationships && !!plan.child_id,
  );

  // The sign-off contact is the signing party. Since ST-5/S15 the DTO also
  // carries the ACTORS — `updated_by` (completion) and `sign_off_recorded_by` +
  // `sign_off_channel` (sign-off) — but this card stays a requirements
  // checklist: the actors are rendered by `LifecycleTimeline`, so completion
  // still shows only its date here and never fabricates an actor.
  const signOffContactName = useMemo(() => {
    if (!plan.sign_off_contact_id) return undefined;
    if (!canReadRelationships) return notAvailableLabel;
    if (!Array.isArray(relationships)) return undefined;
    const match = relationships.find(
      (r: { contactId?: string }) => r.contactId === plan.sign_off_contact_id,
    ) as { contactFirstname?: string; contactLastname?: string } | undefined;
    if (!match) return undefined;
    return `${match.contactFirstname || ''} ${match.contactLastname || ''}`.trim() || undefined;
  }, [canReadRelationships, notAvailableLabel, relationships, plan.sign_off_contact_id]);

  /**
   * Scheduled days on or before today with no tracking entry for the same
   * org-zone date. Joined by org-zone calendar date (never a browser-local one);
   * future-dated entries are not counted, matching the backend's
   * `future_schedule_entries` semantics. "Excused" has no representation in the
   * data model, so an unrecorded past scheduled day is the only unmet condition.
   */
  const missingActuals = useMemo(() => {
    const today = toOrgDateOnly(new Date().toISOString());
    const trackedDates = new Set(
      trackingEntries.map((e) => toOrgDateOnly(e.date)).filter(Boolean),
    );
    return scheduleEntries.filter((e) => {
      const dateKey = toOrgDateOnly(e.date);
      return !!dateKey && dateKey <= today && !trackedDates.has(dateKey);
    }).length;
  }, [scheduleEntries, trackingEntries]);

  const isCompleted = plan.settling_in_completed === true;
  const isSignedOff = !!plan.sign_off_date;
  const isClosed = isCompleted && isSignedOff;

  // The preview says whether this contract's invoices are held (open plan, new
  // contract) and which period's invoice carries the fee (closed plan), both by
  // the engine's initial-phase rule (#1697).
  const {
    data: previewData,
    isSuccess: previewLoaded,
    isError: previewFailed,
  } = useSettlingInBillingPreview(plan.id ?? '', !!plan.id);
  // A contract that already pays is billed as usual during a later settling-in.
  // While the preview is unknown (failed), keep the hint: better an extra note
  // than a missing one.
  const showHoldHint = !isClosed && (previewData?.invoices_held === true || previewFailed);
  // A subsidised contract bills its settling-in days as regular care: no fee
  // lands on any invoice, so the fee notes give way to the no-fee line (#1703).
  // A failed preview cannot tell, and keeps the generic fee note.
  const feeWaivedSubsidised = previewData?.settling_in_fee_applies === false;

  const requirements: Requirement[] = [
    {
      key: 'endDate',
      label: i18n._({ id: 'settlingIn.closure.req.endDate', message: 'End date' }),
      state: plan.end_date ? 'met' : 'unmet',
      metDetail: plan.end_date ? formatDate(plan.end_date, i18n.locale) : undefined,
      actionLabel: i18n._({ id: 'settlingIn.closure.action.setEndDate', message: 'Set end date' }),
      onAction: () => focusFieldOnTab('end_date'),
    },
    {
      key: 'scheduleCreated',
      label: i18n._({ id: 'settlingIn.closure.req.scheduleCreated', message: 'Schedule created' }),
      state: scheduleEntries.length > 0 ? 'met' : 'unmet',
      metDetail:
        scheduleEntries.length > 0
          ? i18n._({
              id: 'settlingIn.closure.scheduleCount',
              message: '{count, plural, one {# day scheduled} other {# days scheduled}}',
              values: { count: scheduleEntries.length },
            })
          : undefined,
      actionLabel: i18n._({ id: 'settlingIn.closure.action.goToDays', message: 'Go to Days' }),
      onAction: () => onGoToTab('days'),
    },
    {
      key: 'infoConfirmed',
      label: i18n._({ id: 'settlingIn.closure.req.infoConfirmed', message: 'Process info confirmed' }),
      state: plan.info_confirmed === true ? 'met' : 'unmet',
      // No actor/date field exists for this flag — met shows the state alone.
      // Names the tab it actually lands on (`focusFieldOnTab` -> 'overview').
      // "Go to Plan" named no tab that exists; the other five locales already
      // said Overview, so English was the outlier.
      actionLabel: i18n._({ id: 'settlingIn.closure.action.goToPlan', message: 'Go to Overview' }),
      onAction: () => focusFieldOnTab('info_confirmed'),
    },
    {
      key: 'allDaysRecorded',
      label: i18n._({ id: 'settlingIn.closure.req.allDaysRecorded', message: 'All days have actuals' }),
      state: missingActuals === 0 ? 'met' : 'unmet',
      metDetail:
        missingActuals === 0
          ? i18n._({ id: 'settlingIn.closure.allDaysRecorded', message: 'All days recorded' })
          : undefined,
      // The visible label carries the missing count; the accessible name says
      // where the jump goes, since "3 days missing actuals" alone does not.
      actionLabel:
        missingActuals > 0
          ? i18n._({
              id: 'settlingIn.closure.missingActuals',
              message: '{count, plural, one {# day missing actuals} other {# days missing actuals}}',
              values: { count: missingActuals },
            })
          : undefined,
      actionAriaLabel: i18n._({
        id: 'settlingIn.closure.action.goToDays',
        message: 'Go to Days',
      }),
      onAction: () => onGoToTab('days'),
    },
    {
      key: 'staffCompletion',
      label: i18n._({ id: 'settlingIn.closure.req.staffCompletion', message: 'Staff completion' }),
      state: isCompleted ? 'met' : 'unmet',
      // Date only. The completion actor lives in `updated_by` since ST-5/S15 and
      // is rendered by `LifecycleTimeline`, not duplicated into this checklist.
      metDetail: isCompleted && plan.end_date ? formatDate(plan.end_date, i18n.locale) : undefined,
      actionLabel: i18n._({ id: 'settlingIn.closure.action.complete', message: 'Complete' }),
      onAction: onComplete,
      onEdit: onComplete,
      editAriaLabel: i18n._({
        id: 'settlingIn.closure.action.editCompletion',
        message: 'Edit completion',
      }),
    },
    {
      key: 'parentSignOff',
      label: i18n._({ id: 'settlingIn.closure.req.parentSignOff', message: 'Parent sign-off' }),
      state: isSignedOff ? 'met' : 'unmet',
      metDetail: isSignedOff
        ? [signOffContactName, formatDate(plan.sign_off_date, i18n.locale)].filter(Boolean).join(' · ')
        : undefined,
      actionLabel: i18n._({ id: 'settlingIn.closure.action.record', message: 'Record' }),
      onAction: onSignOff,
      onEdit: onSignOff,
      editAriaLabel: i18n._({
        id: 'settlingIn.closure.action.editSignOff',
        message: 'Edit sign-off',
      }),
    },
  ];

  function focusFieldOnTab(fieldName: string) {
    onGoToTab('overview');
    // The tab content mounts on the next paint; focus once it exists.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[name="${fieldName}"]`);
      el?.focus();
    });
  }

  // The fee lands on the period the billing preview names as
  // `first_invoice_period` ("YYYY-MM", #1697): the period containing the first
  // billing day for a new contract, the sign-off period for one that already
  // pays. Shown as the MONTH -- "September
  // 2026", not a day, which would read as the invoice date. The sign-off month is
  // the fallback only once the preview has answered without a period; while it
  // loads nothing is named, and a failed preview names no month at all, so the
  // card never shows a month it will then take back.
  const firstInvoicePeriod = previewData?.first_invoice_period;
  const feePeriod = useMemo(() => {
    const dateLocale = getDateFnsLocale(i18n.locale);
    if (firstInvoicePeriod) {
      const period = parse(firstInvoicePeriod, 'yyyy-MM', new Date());
      if (isValid(period)) return format(period, 'MMMM yyyy', { locale: dateLocale });
    }
    if (!previewLoaded) return '';
    const signOff = parseDateString(plan.sign_off_date);
    if (!signOff) return '';
    return format(signOff, 'MMMM yyyy', { locale: dateLocale });
  }, [firstInvoicePeriod, previewLoaded, plan.sign_off_date, i18n.locale]);

  return (
    <div className={cn('rounded-lg border bg-card p-4 space-y-3', className)}>
      <h3 className="text-sm font-semibold">
        {i18n._({ id: 'settlingIn.closure.title', message: 'Closure requirements' })}
      </h3>

      <ul className="space-y-2">
        {requirements.map((req) => {
          const Icon = STATE_ICON[req.state];
          return (
            <li key={req.key} className="flex items-start gap-2 text-sm">
              <Icon
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  req.state === 'met'
                    ? 'text-capacity-normal-on-surface'
                    : 'text-muted-foreground',
                )}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                {/* `block`, so the detail line and the action never run inline
                    into the label ("Staff completionComplete →"). */}
                <span
                  className={cn(
                    'block',
                    req.state === 'met' ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {req.label}
                </span>
                {req.state === 'met' ? (
                  <>
                    {req.metDetail && (
                      <span className="block text-xs text-muted-foreground">{req.metDetail}</span>
                    )}
                    {/* A recorded completion/sign-off is still correctable and
                        undoable — both live inside the dialog this reopens. The
                        header button that recorded it is hidden once recorded, so
                        this is the only remaining way in. */}
                    {req.onEdit && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs flex items-center gap-1"
                        onClick={req.onEdit}
                        aria-label={req.editAriaLabel}
                      >
                        <PenLine className="h-3 w-3" aria-hidden="true" />
                        {i18n._({ id: 'settlingIn.closure.action.edit', message: 'Edit' })}
                      </Button>
                    )}
                  </>
                ) : (
                  req.actionLabel && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs block"
                      onClick={req.onAction}
                      aria-label={req.actionAriaLabel}
                    >
                      {req.actionLabel} →
                    </Button>
                  )
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Advisory notes: they inform, they never disable anything. A failed load
          is itself an advisory — silence here would read as "no warnings". */}
      {(warningsFailed || warnings.length > 0) && (
        <ul className="space-y-1 border-t pt-2" data-testid="closure-advisories">
          {warningsFailed ? (
            <li
              className="flex items-start gap-2 text-xs text-capacity-warning-on-surface"
              data-testid="closure-advisories-error"
            >
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>
                {i18n._({
                  id: 'settlingIn.closure.warningsLoadFailed',
                  message: 'Completion warnings could not be loaded, so this list may be incomplete',
                })}
              </span>
            </li>
          ) : (
            warnings.map((warning) => (
              <li
                key={warning.type}
                className="flex items-start gap-2 text-xs text-capacity-warning-on-surface"
              >
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                <span>
                  {warning.type === settlingin_CompletePlanWarningDTO.type.SIGN_OFF_MISSING &&
                    i18n._({
                      id: 'settlingIn.complete.warning.signOffMissing',
                      message: 'Parent/contact has not signed off yet',
                    })}
                  {warning.type === settlingin_CompletePlanWarningDTO.type.FUTURE_SCHEDULE_ENTRIES &&
                    i18n._({
                      id: 'settlingIn.complete.warning.futureEntries',
                      message:
                        '{count, plural, one {There is # future schedule entry beyond today} other {There are # future schedule entries beyond today}}',
                      values: { count: warning.count ?? 0 },
                    })}
                </span>
              </li>
            ))
          )}
        </ul>
      )}

      {/* Text, not a button: billing is automatic — no UI may imply a manual step. */}
      {showHoldHint && (
        <p
          className="border-t pt-2 text-xs text-muted-foreground"
          data-testid="closure-invoice-hold-hint"
        >
          {i18n._({
            id: 'settlingIn.closure.invoicesHeld',
            message:
              'Invoices for this contract are held until the team has completed the settling-in and the parents have signed it off.',
          })}
        </p>
      )}
      {isClosed && feeWaivedSubsidised && (
        <p
          className="border-t pt-2 text-xs text-muted-foreground"
          data-testid="closure-no-fee-subsidised"
        >
          {i18n._({
            id: 'settlingIn.closure.noFeeSubsidised',
            message:
              'No settling-in fee: this contract is subsidised, and the settling-in days are billed as regular care.',
          })}
        </p>
      )}
      {isClosed && !feeWaivedSubsidised && feePeriod && (
        <p className="border-t pt-2 text-xs text-muted-foreground">
          {i18n._({
            id: 'settlingIn.closure.feeOnNextInvoice',
            message: 'Settling-in fee will appear on the next invoice for {period}',
            values: { period: feePeriod },
          })}
        </p>
      )}
      {isClosed && !feePeriod && previewFailed && (
        <p className="border-t pt-2 text-xs text-muted-foreground">
          {i18n._({
            id: 'settlingIn.closure.feeOnFirstInvoice',
            message: 'Settling-in fee will appear on the first invoice after the settling-in',
          })}
        </p>
      )}
    </div>
  );
}
